/**
 * Mock data for dev/preview mode.
 * Used when VITE_MOCK=true so the frontend is fully browsable without a backend.
 */

export const MOCK_CLIENT_ID = '00000000-0000-0000-0000-000000000001'
export const MOCK_ORG_NAME  = 'Green Valley Community Foundation'

export const MOCK_PREFERENCES = {
  client_id: MOCK_CLIENT_ID,
  email: 'director@greenvalley.org',
  org_name: MOCK_ORG_NAME,
  state: 'NC',
  mission: 'We support youth literacy and workforce development programs in rural North Carolina, serving communities with limited access to educational resources.',
  focus_areas: ['ED', 'ELT', 'CD'],
  digest_frequency: 'weekly',
  typical_grant_min: 5000,
  typical_grant_max: 50000,
  plan_tier: 'growth',
  plan_status: 'trial',
  email_status: 'ok',
  email_status_detail: null,
}

export const MOCK_GRANTS = [
  {
    grant_id: '11111111-0000-0000-0000-000000000001',
    opportunity_id: '350123',
    source: 'grants_gov',
    title: 'Youth Literacy and Education Innovation Grant',
    agency_name: 'Department of Education',
    funding_categories: ['ED'],
    award_floor: 1000000,      // $10,000
    award_ceiling: 5000000,    // $50,000
    close_date: (() => {
      const d = new Date(); d.setDate(d.getDate() + 18); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    })(),
    days_left: 18,
    urgency: 'urgent',
    score: 82,
    feedback: null,
    why_matched: 'This grant aligns with your focus areas and closely matches your mission statement.',
    opportunity_url: 'https://www.grants.gov/search-results-detail/350123',
  },
  {
    grant_id: '11111111-0000-0000-0000-000000000002',
    opportunity_id: '387456',
    source: 'grants_gov',
    title: 'Community Development Block Grant — Rural Communities',
    agency_name: 'Dept. of Housing and Urban Development',
    funding_categories: ['CD'],
    award_floor: 2500000,
    award_ceiling: 25000000,
    close_date: (() => {
      const d = new Date(); d.setDate(d.getDate() + 44); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    })(),
    days_left: 44,
    urgency: 'soon',
    score: 67,
    feedback: null,
    why_matched: 'Matches keywords in your mission statement and aligns with your focus areas.',
    opportunity_url: 'https://www.grants.gov/search-results-detail/387456',
  },
  {
    grant_id: '11111111-0000-0000-0000-000000000003',
    opportunity_id: '412009',
    source: 'grants_gov',
    title: 'Workforce Innovation and Opportunity Act — Youth Formula Grants',
    agency_name: 'Dept. of Labor, Employment & Training',
    funding_categories: ['ELT'],
    award_floor: 5000000,
    award_ceiling: 50000000,
    close_date: (() => {
      const d = new Date(); d.setDate(d.getDate() + 72); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    })(),
    days_left: 72,
    urgency: 'open',
    score: 54,
    feedback: 'relevant',
    why_matched: 'Aligns with your focus areas and award size fits your typical grant range.',
    opportunity_url: 'https://www.grants.gov/search-results-detail/412009',
  },
  {
    grant_id: '11111111-0000-0000-0000-000000000004',
    opportunity_id: '398771',
    source: 'grants_gov',
    title: 'AmeriCorps State and National — Education Priority',
    agency_name: 'AmeriCorps',
    funding_categories: ['ED', 'CD'],
    award_floor: 500000,
    award_ceiling: 3000000,
    close_date: null,
    days_left: null,
    urgency: 'unknown',
    score: 48,
    feedback: null,
    why_matched: 'Aligns with your focus areas.',
    opportunity_url: 'https://www.grants.gov/search-results-detail/398771',
  },
]

// Foundation leads (Phase 9C). Shape mirrors GET /api/funder-matches.
// Set has_access: false (+ waiting_count) to preview the Starter teaser.
export const MOCK_FUNDER_MATCHES = {
  has_access: true,
  waiting_count: 0,
  funders: [
    {
      funder_id: '22222222-0000-0000-0000-000000000001',
      name: 'The Oak Hollow Charitable Foundation',
      city: 'Charlotte',
      state: 'NC',
      typical_grant: '$5,000 – $40,000',
      grants_paid_total: 182500000, // $1.825M in cents
      filing_year: 2024,
      score: 96,
      why_matched:
        'This foundation funds your focus areas, gave 74% of its recent grants in NC and its typical grant size fits your range.',
      application_info:
        'Submit a letter of inquiry with organization background, program description, and budget. Full proposals by invitation.',
      application_deadlines: 'March 1 and September 1 annually',
      deadlines_stale: false,
      application_contact: 'Jane Ellis, 500 Trade St, Charlotte, NC 28202',
      restrictions: 'Giving limited to organizations serving North Carolina.',
      website: null,
      feedback: null,
    },
    {
      funder_id: '22222222-0000-0000-0000-000000000002',
      name: 'Piedmont Education Trust',
      city: 'Raleigh',
      state: 'NC',
      typical_grant: 'Around $15,000',
      grants_paid_total: 61000000,
      filing_year: 2024,
      score: 88,
      why_matched:
        'This foundation funds your focus areas and its grant purposes echo your mission.',
      application_info: 'Applications accepted by mail; no required form.',
      application_deadlines: 'For 2025 due March 5th 2025 at 5pm',
      deadlines_stale: true,
      application_contact: 'Grants Committee, PO Box 1201, Raleigh, NC 27602',
      restrictions: null,
      website: null,
      feedback: null,
    },
  ],
}

export const MOCK_DIGEST_HISTORY = [
  {
    id: 'dl-001',
    sent_at: (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) })(),
    status: 'sent',
    grants_count: 0,
    funders_count: 8,   // foundation-only send — the normal case
  },
  {
    id: 'dl-002',
    sent_at: (() => { const d = new Date(); d.setDate(d.getDate() - 14); return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) })(),
    status: 'sent',
    grants_count: 2,
    funders_count: 8,   // both sections
  },
  {
    id: 'dl-003',
    sent_at: (() => { const d = new Date(); d.setDate(d.getDate() - 21); return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) })(),
    status: 'sent',
    grants_count: 4,
    funders_count: null,  // sent before migration 0018 recorded leads
  },
]

// One foundation lead's full profile (GET /api/funders/:id) — the same funder
// as MOCK_FUNDER_MATCHES[0], with the giving history the card has no room for.
export const MOCK_FUNDER_PROFILE = {
  // Matched: why_not says so, the feedback thumbs render, and no
  // not-in-your-digest banner appears. Flip `matched` to false to preview the
  // corpus-funder shape a Pro client reaches from All Foundations.
  why_not: {
    matched: true,
    reasons: ['matched'],
    explanation: 'This foundation is one of your matches.',
  },
  funder_id: '22222222-0000-0000-0000-000000000001',
  name: 'The Oak Hollow Charitable Foundation',
  funder_type: null,
  verify_note: null,
  city: 'Charlotte',
  state: 'NC',
  website: 'https://www.oakhollowfoundation.org',
  website_display: 'oakhollowfoundation.org',
  ein: '56-1234567',
  ein_url: 'https://projects.propublica.org/nonprofits/organizations/561234567',
  typical_grant: '$5,000 – $40,000',
  total_giving: '$2.4M',
  grants_paid_count: 37,
  grants_paid_total: '$1.8M',
  median_grant: '$15,000',
  recipient_states: ['NC', 'SC', 'VA'],
  application_info:
    'Submit a letter of inquiry with organization background, program description, and budget. Full proposals by invitation.',
  application_deadlines: 'For 2025 due March 5th 2025 at 5pm',
  deadlines_stale: true,
  application_contact: 'Jane Ellis, 500 Trade St, Charlotte, NC 28202',
  restrictions: 'Giving limited to organizations serving North Carolina.',
  filing_year: 2024,
  score: 96,
  why_matched:
    'This foundation funds your focus areas, gave 74% of its recent grants in NC and its typical grant size fits your range.',
  feedback: null,
  grants_shown: 5,
  grants_available: 37,
  grants: [
    {
      recipient: 'Harbor Youth Project',
      recipient_city: 'Durham',
      recipient_state: 'NC',
      amount: '$40,000',
      amount_cents: 4000000,
      year: 2024,
      purpose: 'After-school literacy programming',
    },
    {
      recipient: 'Green Valley Community Center',
      recipient_city: 'Greensboro',
      recipient_state: 'NC',
      amount: '$25,000',
      amount_cents: 2500000,
      year: 2024,
      purpose: 'General operating support',
    },
    {
      recipient: 'Piedmont Food Rescue',
      recipient_city: 'Charlotte',
      recipient_state: 'NC',
      amount: '$15,000',
      amount_cents: 1500000,
      year: 2024,
      purpose: 'Emergency food distribution',
    },
    {
      recipient: 'Carolina Arts Alliance',
      recipient_city: 'Raleigh',
      recipient_state: 'NC',
      amount: null,
      amount_cents: null,
      year: 2024,
      purpose: 'Youth arts education',
    },
    {
      recipient: 'Upstate Shelter Network',
      recipient_city: 'Greenville',
      recipient_state: 'SC',
      amount: '$10,000',
      amount_cents: 1000000,
      year: 2023,
      purpose: null,
    },
  ],
}

// Corpus search (#60 Phase 2a). Covers every why_not branch on purpose — the
// page's whole job is explaining absence, so a mock showing only matched
// funders would exercise none of it.
export const MOCK_CORPUS_SEARCH = {
  total: 6,
  page: 1,
  page_size: 20,
  window_capped: false,
  results: [
    {
      funder_id: '33333333-0000-0000-0000-000000000001',
      name: 'The Oak Hollow Charitable Foundation',
      city: 'Charlotte', state: 'NC',
      typical_grant: '$5,000 – $40,000', total_giving: '$1,825,000',
      grants_paid_count: 61, recipient_states: ['NC', 'SC', 'VA'],
      focus_areas: ['ED', 'HL'], filing_year: 2024,
      application_deadlines: 'March 1 and September 1 annually',
      deadlines_stale: false, deadlines_year_round: false,
      accepts_applications: true, website: null, website_display: null,
      ein: '56-1234567', ein_url: null, score: 96,
      already_funded_known: true, already_funded: false,
      already_funded_grants: 0, already_funded_latest_year: null,
      already_funded_total: null,
      why_not: { matched: true, reasons: ['matched'],
        explanation: 'This foundation is one of your matches.' },
    },
    {
      funder_id: '33333333-0000-0000-0000-000000000002',
      name: 'Cardinal Family Fund',
      city: 'Durham', state: 'NC',
      typical_grant: 'Around $12,000', total_giving: '$640,000',
      grants_paid_count: 38, recipient_states: ['NC'],
      focus_areas: ['ED'], filing_year: 2024,
      application_deadlines: null, deadlines_stale: false,
      deadlines_year_round: false, accepts_applications: true,
      website: 'https://example.org', website_display: 'example.org',
      ein: '56-7654321', ein_url: null, score: null,
      already_funded_known: true, already_funded: true,
      already_funded_grants: 3, already_funded_latest_year: 2023,
      already_funded_total: '$47,500',
      why_not: { matched: false, reasons: ['below_match_threshold'],
        explanation:
          'Not in your digest because it scored below the threshold for a lead — matching is deliberately conservative.' },
    },
    {
      funder_id: '33333333-0000-0000-0000-000000000003',
      name: 'Hartwell Private Trust',
      city: 'Boston', state: 'MA',
      typical_grant: 'Around $250,000', total_giving: '$9,400,000',
      grants_paid_count: 22, recipient_states: ['MA', 'NY'],
      focus_areas: ['AR'], filing_year: 2024,
      application_deadlines: null, deadlines_stale: false,
      deadlines_year_round: false, accepts_applications: false,
      website: null, website_display: null, ein: '04-1112223',
      ein_url: null, score: null,
      already_funded_known: true, already_funded: false,
      already_funded_grants: 0, already_funded_latest_year: null,
      already_funded_total: null,
      why_not: { matched: false, reasons: ['not_open'],
        explanation:
          'Not in your digest because its filing says it does not accept unsolicited applications.' },
    },
    {
      funder_id: '33333333-0000-0000-0000-000000000004',
      name: 'Old Mill Community Foundation',
      city: 'Akron', state: 'OH',
      typical_grant: 'Around $8,000', total_giving: '$310,000',
      grants_paid_count: 15, recipient_states: ['OH'],
      focus_areas: ['CD'], filing_year: 2019,
      application_deadlines: 'For 2020 due March 5th 2020 at 5pm',
      deadlines_stale: true, deadlines_year_round: false,
      accepts_applications: true, website: null, website_display: null,
      ein: '34-9998887', ein_url: null, score: null,
      already_funded_known: true, already_funded: false,
      already_funded_grants: 0, already_funded_latest_year: null,
      already_funded_total: null,
      why_not: { matched: false,
        reasons: ['stale_filing', 'no_giving_recorded_in_your_state'],
        explanation:
          'Not in your digest because its most recent filing on record is from 2019, and its filings record no giving in NC.' },
    },
    {
      funder_id: '33333333-0000-0000-0000-000000000005',
      name: 'Western Slope Legacy Foundation',
      city: 'Denver', state: 'CO',
      typical_grant: 'Around $60,000', total_giving: '$2,100,000',
      grants_paid_count: 44, recipient_states: ['CO', 'UT', 'WY'],
      focus_areas: ['ENV', 'AG'], filing_year: 2023,
      application_deadlines: 'Applications accepted year-round',
      deadlines_stale: false, deadlines_year_round: true,
      accepts_applications: true, website: null, website_display: null,
      ein: '84-5556667', ein_url: null, score: null,
      already_funded_known: true, already_funded: false,
      already_funded_grants: 0, already_funded_latest_year: null,
      already_funded_total: null,
      why_not: { matched: false, reasons: ['no_giving_recorded_in_your_state'],
        explanation:
          'Not in your digest because its filings record no giving in NC.' },
    },
    {
      funder_id: '33333333-0000-0000-0000-000000000006',
      name: 'Anonymous Bequest Trust',
      city: null, state: null,
      typical_grant: null, total_giving: null,
      grants_paid_count: null, recipient_states: null,
      focus_areas: null, filing_year: 2017,
      application_deadlines: null, deadlines_stale: false,
      deadlines_year_round: false, accepts_applications: false,
      website: null, website_display: null, ein: null, ein_url: null,
      score: null,
      // No EIN on file for the client — "have they funded you" is UNKNOWN,
      // and the card must say nothing rather than implying "no".
      already_funded_known: false, already_funded: false,
      already_funded_grants: 0, already_funded_latest_year: null,
      already_funded_total: null,
      why_not: { matched: false, reasons: ['not_open', 'stale_filing'],
        explanation:
          'Not in your digest because its filing says it does not accept unsolicited applications, and its most recent filing on record is from 2017.' },
    },
  ],
}

// GET /api/co-funders (#61 Phase D). The mock deliberately carries the SUCCESS
// state; the three no-result states are reachable in the browser by editing
// `known` / `in_graph` / `leads` here, because each one is a different sentence
// and they are the states most likely to be got wrong.
//
// `via_funder_name` is a CLIENT fact — one of the client's OWN funders — and is
// kept in its own field, never folded into the funder's name or blurb.
export const MOCK_COFUNDERS = {
  known: true,
  in_graph: true,
  seed_count: 4,
  tier_limit: 8,
  more_available: true,
  leads: [
    {
      funder_id: '44444444-0000-0000-0000-000000000001',
      name: 'United Way Of Massachusetts Bay Inc',
      city: 'Boston', state: 'MA',
      shared_grantees: 39,
      via_funder_name: 'Health Resources In Action Inc',
      accepts_applications: null, filing_year: 2024,
      profile_available: true, evidence_is_local: true,
    },
    {
      funder_id: '44444444-0000-0000-0000-000000000002',
      name: 'Combined Jewish Philanthropies Of Greater Boston',
      city: 'Boston', state: 'MA',
      shared_grantees: 22,
      via_funder_name: 'The Boston Foundation',
      accepts_applications: true, filing_year: 2024,
      profile_available: true, evidence_is_local: true,
    },
    {
      funder_id: '44444444-0000-0000-0000-000000000003',
      name: 'Cambridge Community Foundation',
      city: 'Cambridge', state: 'MA',
      shared_grantees: 11,
      via_funder_name: 'The Boston Foundation',
      accepts_applications: null, filing_year: 2023,
      // Not matched and the tier has no corpus access — the card shows the
      // lead without a link that would 404.
      profile_available: false,
      // Demoted, not removed: the shared grantees are mostly out of state, so
      // the card says so and ranks it last (#61 Phase D).
      evidence_is_local: false,
    },
  ],
}

// Admin console roster. Deliberately mirrors the shapes prod actually holds —
// a healthy live client, the unmatched-EIN case, an expired confirmation, and a
// ZZ TEST row — so the page's states are all reachable with VITE_MOCK=true.
export const MOCK_ADMIN_CLIENTS = {
  generated_at: '2026-08-20T12:00:00Z',
  total: 3,
  live: 2,
  non_client: 1,
  clients: [
    {
      id: '00000000-0000-0000-0000-0000000000a1',
      email: 'director@riverbend.org',
      org_name: 'Riverbend Youth & Family Center',
      is_test: false, is_operator: false,
      created_at: '2026-07-27T01:30:00Z',
      state: 'MA', city: 'Lowell',
      plan_tier: 'pro', plan_status: 'beta', account_type: 'beta',
      entitled: true, trial_ends_at: null, has_stripe_subscription: false,
      email_status: 'ok', email_status_detail: null,
      email_confirmed: true, unsubscribed: false,
      org_ein: '04-2121342', ein_status: 'unmatched', ein_filing_count: 0,
      mission_chars: 412, has_embedding: true, focus_area_count: 4,
      federal_matches: 4, foundation_matches: 6834,
      digest_count: 7, last_digest_at: '2026-08-18T08:00:01Z',
      health: [
        {
          code: 'ein_unmatched', severity: 'warn',
          message: 'EIN 04-2121342 is well-formed but appears in NO funder-grant filings. '
            + 'Because it passes validation, peer evidence reports "no funder has funded you" '
            + 'rather than "unknown" — an unverified claim. Confirm it is really this '
            + "organization's EIN.",
        },
      ],
    },
    {
      id: '00000000-0000-0000-0000-0000000000a2',
      email: 'hello@harborhouse.org',
      org_name: 'Harbor House Services',
      is_test: false, is_operator: false,
      created_at: '2026-08-19T14:02:00Z',
      state: 'MA', city: 'Quincy',
      plan_tier: 'starter', plan_status: 'trial', account_type: 'standard',
      entitled: true, trial_ends_at: '2026-09-02T14:02:00Z', has_stripe_subscription: false,
      email_status: 'unconfirmed', email_status_detail: null,
      email_confirmed: false, unsubscribed: false,
      org_ein: null, ein_status: 'missing', ein_filing_count: 0,
      mission_chars: 88, has_embedding: false, focus_area_count: 0,
      federal_matches: 0, foundation_matches: 0,
      digest_count: 0, last_digest_at: null,
      health: [
        { code: 'thin_mission', severity: 'info', message: 'Mission is only 88 characters. Matching reads it as the whole description of the org; 3–4 sentences works considerably better.' },
        { code: 'no_embedding', severity: 'warn', message: 'Mission is on file but the client has no embedding — the semantic half of matching contributes nothing until task_embed_client runs.' },
        { code: 'no_focus_areas', severity: 'warn', message: 'No focus areas selected — the category filters have nothing to match.' },
        { code: 'ein_missing', severity: 'info', message: 'No EIN on file — peer evidence and the co-funder graph both answer "we can\'t check" for this client.' },
        { code: 'confirmation_expired', severity: 'warn', message: 'Never confirmed. The link was sent 4d ago and expired after 48h — this signup is lost unless a new one is sent.' },
        { code: 'no_foundation_matches', severity: 'warn', message: 'No foundation matches stored. Foundations carry the digest, so this client\'s next send would be effectively empty.' },
      ],
    },
    {
      id: '00000000-0000-0000-0000-0000000000a3',
      email: 'zztest-nrwa@example.invalid',
      org_name: 'ZZ TEST — Neponset River Watershed Alliance',
      is_test: true, is_operator: false,
      created_at: '2026-07-18T10:00:00Z',
      state: 'MA', city: 'Canton',
      plan_tier: 'pro', plan_status: 'cancelled', account_type: 'standard',
      entitled: false, trial_ends_at: null, has_stripe_subscription: false,
      email_status: 'unconfirmed', email_status_detail: null,
      email_confirmed: false, unsubscribed: false,
      org_ein: null, ein_status: 'missing', ein_filing_count: 0,
      mission_chars: 260, has_embedding: true, focus_area_count: 2,
      federal_matches: 5, foundation_matches: 6287,
      digest_count: 0, last_digest_at: null,
      health: [
        { code: 'ein_missing', severity: 'info', message: 'No EIN on file — peer evidence and the co-funder graph both answer "we can\'t check" for this client.' },
        { code: 'confirmation_expired', severity: 'warn', message: 'Never confirmed. The link was sent 33d ago and expired after 48h — this signup is lost unless a new one is sent.' },
      ],
    },
  ],
}
